import authService from './authService';
import { auth } from '../services/firebase/config';
import { markdownToHtml } from '../utils/markdownTiptap';
import { getGraphClient } from './graphClient';

/**
 * Service pour l'envoi d'emails via Microsoft Graph
 */
class EmailService {
  constructor() {
    this.firebaseApiUrl = 'https://us-central1-diotranscript.cloudfunctions.net/sendEmail';
  }

  /**
   * Obtient le token Firebase de l'utilisateur actuel
   * @returns {Promise<string>} - Le token Firebase
   */
  async getFirebaseToken() {
    if (!auth.currentUser) {
      throw new Error('Utilisateur non connecté');
    }
    return await auth.currentUser.getIdToken(true);
  }

  /**
   * Obtient un token Azure valide avec fallback intelligent selon les bonnes pratiques Microsoft
   */
  async getValidAzureTokenForEmailSending() {
    try {
        const authService = await import('./authService');
        const azureService = await import('./azureService');

        // Étape 1: Toujours essayer le rafraîchissement silencieux d'abord (recommandation Microsoft)
        try {
            const silentToken = await authService.default.getValidAzureToken();
            if (silentToken) {

                return silentToken;
            }
        } catch (silentError) {

            // Étape 2: Si erreur "interaction_required", utiliser une interaction (recommandation Microsoft)
            if (silentError.name === 'InteractionRequiredAuthError' || 
                silentError.message?.includes('interaction_required') ||
                silentError.message?.includes('User interaction is required')) {

                // Sur mobile, utiliser popup si possible, sinon redirection
                const userAgent = navigator.userAgent.toLowerCase();
                const isMobile = /iphone|ipad|android|mobile/.test(userAgent);
                
                if (isMobile) {

                    try {
                        const popupToken = await azureService.default.acquireTokenPopup();
                        if (popupToken) {

                            return popupToken;
                        }
                    } catch (popupError) {

                        throw new Error(`Impossible d'obtenir le token Azure via popup. ${popupError.message}`);
                    }
                } else {

                    throw new Error('Une nouvelle connexion est nécessaire. Veuillez vous reconnecter.');
                }
            } else {
                // Autres types d'erreur
                throw new Error(`Erreur d'authentification: ${silentError.message}`);
            }
        }
        
        throw new Error('Impossible d\'obtenir un token Azure valide');
        
    } catch (error) {

        throw error;
    }
}

/**
 * Envoie un email via Microsoft Graph
 */
async sendEmail(emailData) {
    try {

        // Obtenir un token Azure valide
        const accessToken = await this.getValidAzureTokenForEmailSending();
        
        if (!accessToken) {
            throw new Error('Token Azure non disponible. Veuillez vous reconnecter.');
        }

        // Préparer les données de l'email
        const emailPayload = {
            message: {
                subject: emailData.subject,
                body: {
                    contentType: "HTML",
                    content: emailData.body
                },
                toRecipients: emailData.recipients.map(email => ({
                    emailAddress: { address: email }
                }))
            },
            saveToSentItems: false
        };

        const client = await getGraphClient();

        await client.api('/me/sendMail').post(emailPayload);

        return {
            success: true,
            message: 'Email envoyé avec succès'
        };

    } catch (error) {

        throw error;
    }
}

/**
 * Envoie des emails à plusieurs destinataires de manière séquentielle
 * avec gestion des erreurs individuelles
 */
async sendEmails(emailData) {
    try {

        if (!emailData.recipients || emailData.recipients.length === 0) {
            throw new Error('Aucun destinataire spécifié');
        }

        const client = await getGraphClient();

        const results = [];
        const succeeded = [];
        const failed = [];

        // Envoyer à chaque destinataire individuellement
        for (const recipient of emailData.recipients) {
            try {

                // Préparer l'email pour ce destinataire
                const singleEmailData = {
                    subject: emailData.subject,
                    body: emailData.body,
                    recipients: [recipient]
                };

                // Préparer le payload pour Microsoft Graph
                const emailPayload = {
                    message: {
                        subject: singleEmailData.subject,
                        body: {
                            contentType: "HTML",
                            content: singleEmailData.body
                        },
                        toRecipients: [{
                            emailAddress: { address: recipient }
                        }]
                    },
                    saveToSentItems: false
                };

                // Envoyer via Microsoft Graph directement

                await client.api('/me/sendMail').post(emailPayload);

                // Succès pour ce destinataire
                succeeded.push(recipient);
                results.push({
                    recipient,
                    success: true,
                    message: 'Email envoyé avec succès'
                });

                // Petite pause entre les envois pour éviter de surcharger l'API
                if (emailData.recipients.length > 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }

            } catch (error) {

                failed.push({
                    recipient,
                    error: error.message
                });

                results.push({
                    recipient,
                    success: false,
                    error: error.message
                });
            }
        }

        const summary = {
            total: emailData.recipients.length,
            succeeded: succeeded.length,
            failed: failed.length,
            results,
            successfulRecipients: succeeded,
            failedRecipients: failed
        };

        if (succeeded.length === 0) {
            throw new Error(`Tous les envois ont échoué. Détails: ${failed.map(f => `${f.recipient}: ${f.error}`).join('; ')}`);
        }

        return {
            success: true,
            message: `${succeeded.length} email(s) envoyé(s) avec succès${failed.length > 0 ? `, ${failed.length} échec(s)` : ''}`,
            summary
        };

    } catch (error) {

        throw error;
    }
}

  /**
   * Envoie un email de test
   * @param {string} testEmail - Adresse email de test
   * @returns {Promise<Object>} - Résultat du test
   */
  async sendTestEmail(testEmail) {
    const subject = 'Test d\'envoi d\'email - TranscriptionAI';
    const body = `
      <p>Bonjour,</p>
      <p>Ceci est un email de test envoyé depuis TranscriptionAI.</p>
      <p><strong>Détails du test:</strong></p>
      <ul>
        <li>Date: ${new Date().toLocaleString('fr-FR')}</li>
        <li>Expéditeur: Votre adresse email connectée</li>
      </ul>
      <p>Si vous recevez cet email, la configuration est correcte ✅</p>
      <p>Bien cordialement,<br>L'équipe TranscriptionAI</p>
    `;

    return await this.sendEmail({ 
      recipients: [testEmail], 
      subject, 
      body
    });
  }

  /**
   * Teste l'envoi d'emails à plusieurs destinataires avec rapport détaillé
   * @param {Array} testEmails - Liste des adresses email de test
   * @returns {Promise<Object>} - Résultat détaillé du test
   */
  async testMultipleEmailSend(testEmails) {
    if (!Array.isArray(testEmails) || testEmails.length === 0) {
      throw new Error('Veuillez fournir une liste d\'adresses email pour le test');
    }

    const results = [];
    let successCount = 0;
    let failureCount = 0;
    const startTime = Date.now();

    for (let i = 0; i < testEmails.length; i++) {
      const email = testEmails[i];
      const testStartTime = Date.now();

      try {

        const subject = `Test d'envoi multiple - TranscriptionAI (${i + 1}/${testEmails.length})`;
        const body = `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <h2 style="color: #4CAF50;">🧪 Test d'envoi multiple - TranscriptionAI</h2>
            <p>Bonjour,</p>
            <p>Ceci est un email de test pour vérifier la distribution des emails.</p>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3 style="margin: 0 0 10px 0; color: #007bff;">📊 Détails du test :</h3>
              <ul style="margin: 0; padding-left: 20px;">
                <li><strong>Destinataire :</strong> ${email}</li>
                <li><strong>Position dans la liste :</strong> ${i + 1} sur ${testEmails.length}</li>
                <li><strong>Date d'envoi :</strong> ${new Date().toLocaleString('fr-FR')}</li>
                <li><strong>ID du test :</strong> ${Date.now()}-${i}</li>
              </ul>
            </div>

            <p>Si vous recevez cet email, cela confirme que :</p>
            <ul>
              <li>✅ La connexion Azure fonctionne</li>
              <li>✅ Les permissions d'envoi sont correctes</li>
              <li>✅ La distribution multiple fonctionne</li>
            </ul>
            
            <p><strong>Note :</strong> Si vous êtes le ${i + 1}${i === 0 ? 'er' : 'ème'} destinataire de cette liste de test, tout fonctionne parfaitement !</p>
            
            <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;">
            <p style="font-size: 0.9em; color: #666;">
              Cet email a été généré automatiquement par le système de test de TranscriptionAI.<br>
              L'équipe TranscriptionAI
            </p>
          </div>
        `;

        const result = await this.sendEmail({ 
          recipients: [email], 
          subject, 
          body
        });

        const testEndTime = Date.now();
        const duration = testEndTime - testStartTime;

        results.push({
          email: email,
          success: true,
          result: result,
          duration: duration,
          order: i + 1
        });

        successCount++;
        console.log(`✅ Test ${i + 1} réussi - ${email} (${duration}ms)`);

        // Petite pause entre les envois pour éviter le rate limiting
        if (i < testEmails.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

      } catch (error) {
        const testEndTime = Date.now();
        const duration = testEndTime - testStartTime;

        results.push({
          email: email,
          success: false,
          error: error.message,
          duration: duration,
          order: i + 1
        });

        failureCount++;

        // Pause plus longue en cas d'erreur
        if (i < testEmails.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    const totalTime = Date.now() - startTime;

    const testReport = {
      summary: {
        total: testEmails.length,
        success: successCount,
        failure: failureCount,
        successRate: ((successCount / testEmails.length) * 100).toFixed(1),
        totalDuration: totalTime,
        averageDuration: Math.round(totalTime / testEmails.length)
      },
      results: results,
      recommendation: this.generateTestRecommendation(successCount, failureCount, results)
    };

    return testReport;
  }

  /**
   * Génère des recommandations basées sur les résultats du test
   * @param {number} successCount - Nombre de succès
   * @param {number} failureCount - Nombre d'échecs
   * @param {Array} results - Résultats détaillés
   * @returns {Object} - Recommandations
   */
  generateTestRecommendation(successCount, failureCount, results) {
    const total = successCount + failureCount;
    const successRate = (successCount / total) * 100;

    let status = 'unknown';
    let recommendations = [];

    if (successRate === 100) {
      status = 'excellent';
      recommendations.push('✅ Parfait ! Tous les emails ont été envoyés avec succès.');
      recommendations.push('💡 Le système de distribution fonctionne parfaitement.');
    } else if (successRate >= 80) {
      status = 'good';
      recommendations.push('✅ Bon ! La plupart des emails ont été envoyés.');
      recommendations.push('⚠️ Vérifiez les adresses qui ont échoué pour des problèmes spécifiques.');
    } else if (successRate >= 50) {
      status = 'warning';
      recommendations.push('⚠️ Moyen. Environ la moitié des emails ont été envoyés.');
      recommendations.push('🔍 Vérifiez les permissions et la configuration Azure.');
      recommendations.push('💡 Consultez les logs d\'erreur pour identifier les problèmes.');
    } else {
      status = 'critical';
      recommendations.push('❌ Critique ! La plupart des envois ont échoué.');
      recommendations.push('🚨 Vérifiez la connexion Azure et les permissions.');
      recommendations.push('🔧 Consultez la documentation de dépannage.');
    }

    // Analyser les types d'erreurs
    const errors = results.filter(r => !r.success).map(r => r.error);
    const uniqueErrors = [...new Set(errors)];

    if (uniqueErrors.length > 0) {
      recommendations.push('');
      recommendations.push('🔍 Types d\'erreurs détectées :');
      uniqueErrors.forEach(error => {
        const count = errors.filter(e => e === error).length;
        recommendations.push(`• ${error} (${count} fois)`);
      });
    }

    return {
      status: status,
      successRate: successRate,
      recommendations: recommendations
    };
  }

  /**
   * Envoie un email de notification de transcription terminée
   * @param {Object} options - Options de l'email
   * @param {string} options.to - Destinataire
   * @param {string} options.meetingTitle - Titre de la réunion
   * @param {string} options.meetingUrl - URL de la réunion
   * @param {string} options.userDisplayName - Nom d'affichage de l'utilisateur
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  async sendTranscriptionCompleteEmail({ to, meetingTitle, meetingUrl, userDisplayName }) {
    const subject = `Transcription terminée: ${meetingTitle || 'Sans titre'}`;
    const body = `
      <p>Bonjour ${userDisplayName || to},</p>
      <p>La transcription de votre réunion <strong>${meetingTitle || 'Sans titre'}</strong> est maintenant terminée.</p>
      <p>Vous pouvez consulter la transcription en cliquant sur le lien suivant:</p>
      <p><a href="${meetingUrl}">Voir la transcription</a></p>
      <p>Bien cordialement,<br>L'équipe TranscriptionAI</p>
    `;

    return await this.sendEmail({ recipients: [to], subject, body });
  }

  /**
   * Envoie un email avec le résumé d'une réunion
   * @param {Object} options - Options de l'email
   * @param {string} options.to - Destinataire
   * @param {string} options.meetingTitle - Titre de la réunion
   * @param {string} options.meetingDate - Date de la réunion
   * @param {string} options.meetingDuration - Durée de la réunion
   * @param {Array} options.participants - Participants de la réunion
   * @param {string} options.summary - Résumé de la réunion (en markdown)
   * @param {Object} options.summaryStats - Statistiques du résumé (tokens, coût, etc.)
   * @param {string} options.meetingUrl - URL de la réunion
   * @param {string} options.senderName - Nom de l'expéditeur
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  async sendSummaryEmail({ to, meetingTitle, meetingDate, meetingDuration, participants, summary, summaryStats, meetingUrl, senderName }) {
    const subject = `Résumé de réunion: ${meetingTitle || 'Sans titre'}`;
    
    // Convertir le markdown en HTML
    let summaryHtml = 'Aucun résumé disponible.';
    if (summary) {
      try {
        summaryHtml = await markdownToHtml(summary);
      } catch (error) {

        // Fallback: utiliser le markdown brut avec des sauts de ligne
        summaryHtml = summary.replace(/\n/g, '<br/>');
      }
    }
    
    // Formater la date
    const formattedDate = meetingDate ? new Date(meetingDate).toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) : 'Date non disponible';
    
    // Formater les participants
    let participantsText = '';
    if (participants && Array.isArray(participants) && participants.length > 0) {
      participantsText = participants.map(p => {
        if (typeof p === 'string') return p;
        const name = p.displayName || p.name || '';
        const role = p.title || p.role || '';
        return role ? `${name} (${role})` : name;
      }).filter(Boolean).join('<br/>• ');
      participantsText = participantsText ? `• ${participantsText}` : '';
    } else {
      participantsText = 'Aucun participant renseigné';
    }

    // Formater les statistiques
    let statsText = '';
    if (summaryStats && summaryStats.usage) {
      const usage = summaryStats.usage;
      statsText = `
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #007bff;">
          <h4 style="margin: 0 0 10px 0; color: #007bff; font-size: 14px;">📊 Statistiques de génération</h4>
          <ul style="margin: 0; padding-left: 20px; color: #6c757d; font-size: 13px;">
            <li><strong>Modèle IA utilisé:</strong> ${summaryStats.model || 'Non spécifié'}</li>
            <li><strong>Tokens traités:</strong> ${usage.tokens?.total?.toLocaleString() || 'N/A'}</li>
            <li><strong>Coût estimé:</strong> ${usage.cost?.total_eur ? `€${usage.cost.total_eur.toFixed(4)}` : 'N/A'}</li>
          </ul>
        </div>
      `;
    }

    const body = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 700px; margin: 0 auto; padding: 20px; }
          .header { background-color: #667eea; padding: 30px; border-radius: 12px 12px 0 0; text-align: center; }
          .content { background: white; padding: 30px; border: 1px solid #e0e0e0; }
          .footer { background: #f8f9fa; padding: 20px; border-radius: 0 0 12px 12px; text-align: center; border: 1px solid #e0e0e0; border-top: none; }
          .meeting-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .summary-box { background: #fff; border: 1px solid #e9ecef; border-radius: 8px; padding: 25px; margin: 20px 0; }
          h1 { margin: 0; font-size: 24px; font-weight: 600; color: #ffffff; }
          h2 { color: #495057; font-size: 18px; margin: 25px 0 15px 0; border-bottom: 2px solid #007bff; padding-bottom: 8px; }
          .info-row { display: flex; margin: 8px 0; align-items: center; }
          .info-label { font-weight: 600; color: #6c757d; min-width: 120px; }
          .info-value { color: #495057; }
          .summary-content { font-size: 15px; line-height: 1.7; color: #495057; }
          .summary-content h1, .summary-content h2, .summary-content h3 { color: #343a40; margin: 20px 0 10px 0; }
          .summary-content h1 { font-size: 22px; border-bottom: 2px solid #007bff; padding-bottom: 5px; }
          .summary-content h2 { font-size: 18px; border-bottom: 1px solid #dee2e6; padding-bottom: 3px; }
          .summary-content h3 { font-size: 16px; }
          .summary-content ul, .summary-content ol { margin: 10px 0; padding-left: 25px; }
          .summary-content li { margin: 5px 0; }
          .summary-content p { margin: 10px 0; }
          .summary-content strong { color: #495057; font-weight: 600; }
          .summary-content em { font-style: italic; color: #6c757d; }
          .summary-content code { background: #f8f9fa; padding: 2px 4px; border-radius: 3px; font-family: 'Courier New', monospace; color: #e83e8c; }
          .summary-content pre { background: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #007bff; overflow-x: auto; }
          .summary-content blockquote { margin: 15px 0; padding: 10px 20px; background: #f8f9fa; border-left: 4px solid #6c757d; font-style: italic; }
          .summary-content table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          .summary-content th, .summary-content td { border: 1px solid #dee2e6; padding: 8px 12px; text-align: left; }
          .summary-content th { background: #f8f9fa; font-weight: 600; }
          .button { display: inline-block; background-color: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; margin: 20px 0; }
          .signature { color: #6c757d; font-size: 14px; margin-top: 30px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Résumé de réunion</h1>
            <p style="margin: 10px 0 0 0; color: #e0e0ff;">Généré automatiquement par Transcription</p>
          </div>
          
          <div class="content">
            <p>Bonjour,</p>
            <p>Voici le résumé de la réunion <strong>"${meetingTitle || 'Sans titre'}"</strong> partagé par <strong>${senderName || 'un utilisateur'}</strong>.</p>
            
            <div class="meeting-info">
              <h2>ℹ️ Informations de la réunion</h2>
              <div class="info-row">
                <span class="info-label">📅 Date:</span>
                <span class="info-value">${formattedDate}</span>
              </div>
              ${meetingDuration ? `
              <div class="info-row">
                <span class="info-label">⏱️ Durée:</span>
                <span class="info-value">${meetingDuration}</span>
              </div>
              ` : ''}
              <div class="info-row">
                <span class="info-label">👥 Participants:</span>
                <div class="info-value">${participantsText}</div>
              </div>
            </div>

            <div class="summary-box">
              <h2>📋 Résumé</h2>
              <div class="summary-content">${summaryHtml}</div>
            </div>

            ${statsText}

            ${meetingUrl ? `
            <p style="text-align: center; margin: 30px 0;">
              <a href="${meetingUrl}" class="button">🔗 Voir la réunion complète</a>
            </p>
            ` : ''}
          </div>
          
          <div class="footer">
            <div class="signature">
              <p><strong>TranscriptionAI</strong><br/>
              Service de transcription et résumé automatique<br/>
              <small>Cet email a été généré automatiquement.</small></p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    return await this.sendEmail({ recipients: [to], subject, body });
  }

  /**
   * Envoie un email avec la transcription d'une réunion
   * @param {Object} options - Options de l'email
   * @param {string} options.to - Destinataire
   * @param {string} options.meetingTitle - Titre de la réunion
   * @param {string} options.meetingDate - Date de la réunion
   * @param {string} options.meetingDuration - Durée de la réunion
   * @param {Array} options.participants - Participants de la réunion
   * @param {Object} options.transcript - Transcription de la réunion
   * @param {string} options.meetingUrl - URL de la réunion
   * @param {string} options.senderName - Nom de l'expéditeur
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  async sendTranscriptEmail({ to, meetingTitle, meetingDate, meetingDuration, participants, transcript, meetingUrl, senderName }) {
    const subject = `Transcription de réunion: ${meetingTitle || 'Sans titre'}`;
    
    // Formater la transcription en HTML - utiliser la même logique que l'export Word
    let transcriptHtml = 'Aucune transcription disponible.';
    if (transcript) {
      try {
        if (transcript.utterances && Array.isArray(transcript.utterances)) {
          // Format utterances (avec diarisation) - priorité car plus détaillé
          transcriptHtml = transcript.utterances.map(utterance => {
            const speaker = utterance.speaker || 'Inconnu';
            const text = utterance.text || '';
            const timestamp = utterance.start ? this.formatTimestamp(utterance.start / 1000) : '';
            
            return `
              <div style="margin-bottom: 15px; padding: 10px; border-left: 3px solid #28a745; background: #f8f9fa;">
                <div style="font-weight: 600; color: #28a745; margin-bottom: 5px;">
                  👤 ${speaker} ${timestamp ? `(${timestamp})` : ''}
                </div>
                <div style="color: #495057; line-height: 1.6; padding-left: 20px;">
                  ${text}
                </div>
              </div>
            `;
          }).join('');
        } else if (transcript.result?.segments && Array.isArray(transcript.result.segments)) {
          // Transcription avec segments/locuteurs
          transcriptHtml = transcript.result.segments.map(segment => {
            const speaker = segment.speaker || 'Inconnu';
            const text = segment.text || '';
            const timestamp = segment.timestamp ? this.formatTimestamp(segment.timestamp) : '';
            
            return `
              <div style="margin-bottom: 15px; padding: 10px; border-left: 3px solid #28a745; background: #f8f9fa;">
                <div style="font-weight: 600; color: #28a745; margin-bottom: 5px;">
                  👤 ${speaker} ${timestamp ? `(${timestamp})` : ''}
                </div>
                <div style="color: #495057; line-height: 1.6; padding-left: 20px;">
                  ${text}
                </div>
              </div>
            `;
          }).join('');
        } else if (transcript.result?.utterances && Array.isArray(transcript.result.utterances)) {
          // Format utterances (avec diarisation)
          transcriptHtml = transcript.result.utterances.map(utterance => {
            const speaker = utterance.speaker || 'Inconnu';
            const text = utterance.text || '';
            const timestamp = utterance.start ? this.formatTimestamp(utterance.start) : '';
            
            return `
              <div style="margin-bottom: 15px; padding: 10px; border-left: 3px solid #28a745; background: #f8f9fa;">
                <div style="font-weight: 600; color: #28a745; margin-bottom: 5px;">
                  👤 ${speaker} ${timestamp ? `(${timestamp})` : ''}
                </div>
                <div style="color: #495057; line-height: 1.6; padding-left: 20px;">
                  ${text}
                </div>
              </div>
            `;
          }).join('');
        } else if (transcript.fullText) {
          // Transcription simple en texte
          const lines = transcript.fullText.split(/\n+/).filter(line => line.trim());
          transcriptHtml = lines.map(line => `
            <div style="margin-bottom: 10px; padding: 8px; color: #495057; line-height: 1.6;">
              ${line}
            </div>
          `).join('');
        } else if (transcript.result?.words && Array.isArray(transcript.result.words)) {
          // Reconstruction à partir des mots avec diarisation
          let currentSpeaker = null;
          let currentText = '';
          let segments = [];
          
          transcript.result.words.forEach((word, index) => {
            if (word.speaker !== currentSpeaker && index > 0) {
              if (currentSpeaker !== null && currentText.trim()) {
                segments.push({
                  speaker: currentSpeaker,
                  text: currentText.trim()
                });
              }
              currentSpeaker = word.speaker;
              currentText = word.text;
            } else {
              currentSpeaker = word.speaker || currentSpeaker;
              currentText += (currentText ? ' ' : '') + word.text;
            }
          });
          
          // Ajouter le dernier segment
          if (currentSpeaker !== null && currentText.trim()) {
            segments.push({
              speaker: currentSpeaker,
              text: currentText.trim()
            });
          }
          
          transcriptHtml = segments.map(segment => `
            <div style="margin-bottom: 15px; padding: 10px; border-left: 3px solid #28a745; background: #f8f9fa;">
              <div style="font-weight: 600; color: #28a745; margin-bottom: 5px;">
                👤 ${segment.speaker}
              </div>
              <div style="color: #495057; line-height: 1.6; padding-left: 20px;">
                ${segment.text}
              </div>
            </div>
          `).join('');
        } else {
          transcriptHtml = '<p style="color: #718096; font-style: italic;">Format de transcription non reconnu.</p>';
        }
      } catch (error) {

        transcriptHtml = '<p style="color: #e53e3e;">Erreur lors du formatage de la transcription.</p>';
      }
    }
    
    // Formater la date
    const formattedDate = meetingDate ? new Date(meetingDate).toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) : 'Date non disponible';
    
    // Formater les participants
    let participantsText = '';
    if (participants && Array.isArray(participants) && participants.length > 0) {
      participantsText = participants.map(p => {
        if (typeof p === 'string') return p;
        const name = p.displayName || p.name || '';
        const role = p.title || p.role || '';
        return role ? `${name} (${role})` : name;
      }).filter(Boolean).join('<br/>• ');
      participantsText = participantsText ? `• ${participantsText}` : '';
    } else {
      participantsText = 'Aucun participant renseigné';
    }

    const body = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 700px; margin: 0 auto; padding: 20px; }
          .header { background-color: #28a745; padding: 30px; border-radius: 12px 12px 0 0; text-align: center; }
          .content { background: white; padding: 30px; border: 1px solid #e0e0e0; }
          .footer { background: #f8f9fa; padding: 20px; border-radius: 0 0 12px 12px; text-align: center; border: 1px solid #e0e0e0; border-top: none; }
          .meeting-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .transcript-box { background: #fff; border: 1px solid #e9ecef; border-radius: 8px; padding: 25px; margin: 20px 0; max-height: 500px; overflow-y: auto; }
          h1 { margin: 0; font-size: 24px; font-weight: 600; color: #ffffff; }
          h2 { color: #495057; font-size: 18px; margin: 25px 0 15px 0; border-bottom: 2px solid #28a745; padding-bottom: 8px; }
          .info-row { display: flex; margin: 8px 0; align-items: center; }
          .info-label { font-weight: 600; color: #6c757d; min-width: 120px; }
          .info-value { color: #495057; }
          .button { display: inline-block; background-color: #28a745; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; margin: 20px 0; }
          .signature { color: #6c757d; font-size: 14px; margin-top: 30px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Transcription de réunion</h1>
            <p style="margin: 10px 0 0 0; color: #c8f7c5;">Généré automatiquement par Transcription</p>
          </div>
          
          <div class="content">
            <p>Bonjour,</p>
            <p>Voici la transcription de la réunion <strong>"${meetingTitle || 'Sans titre'}"</strong> partagée par <strong>${senderName || 'un utilisateur'}</strong>.</p>
            
            <div class="meeting-info">
              <h2>ℹ️ Informations de la réunion</h2>
              <div class="info-row">
                <span class="info-label">📅 Date:</span>
                <span class="info-value">${formattedDate}</span>
              </div>
              ${meetingDuration ? `
              <div class="info-row">
                <span class="info-label">⏱️ Durée:</span>
                <span class="info-value">${meetingDuration}</span>
              </div>
              ` : ''}
              <div class="info-row">
                <span class="info-label">👥 Participants:</span>
                <div class="info-value">${participantsText}</div>
              </div>
            </div>

            <div class="transcript-box">
              <h2>📝 Transcription complète</h2>
              ${transcriptHtml}
            </div>

            ${meetingUrl ? `
            <p style="text-align: center; margin: 30px 0;">
              <a href="${meetingUrl}" class="button">🔗 Voir la réunion complète</a>
            </p>
            ` : ''}
          </div>
          
          <div class="footer">
            <div class="signature">
              <p><strong>TranscriptionAI</strong><br/>
              Service de transcription et résumé automatique<br/>
              <small>Cet email a été généré automatiquement.</small></p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    return await this.sendEmail({ recipients: [to], subject, body });
  }

  /**
   * Formate un timestamp en minutes:secondes
   * @param {number} seconds - Timestamp en secondes
   * @returns {string} - Timestamp formaté
   */
  formatTimestamp(seconds) {
    if (!seconds || typeof seconds !== 'number') return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

// Instance singleton
const emailService = new EmailService();

export default emailService; 